import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { 
  CheckCircle, 
  Error, 
  Warning, 
  Info, 
  Schedule,
  PlayArrow,
  Pause
} from '@mui/icons-material';

export type StatusType = 'pending' | 'running' | 'completed' | 'error' | 'warning' | 'info' | 'paused';

export interface StatusIconProps {
  status: StatusType;
  size?: 'small' | 'medium' | 'large';
  showProgress?: boolean;
  sx?: any;
}

const StatusIcon = ({
  status,
  size = 'medium',
  showProgress = false,
  sx,
}: StatusIconProps) => {
  const getSizeValue = () => {
    switch (size) {
      case 'small': return 16;
      case 'large': return 32;
      default: return 24;
    }
  };

  const getStatusConfig = () => {
    const iconSize = getSizeValue();
    
    switch (status) {
      case 'pending':
        return {
          icon: <Schedule sx={{ fontSize: iconSize }} />,
          color: 'grey.500',
          bgColor: 'grey.100',
        };
      case 'running':
        return {
          icon: showProgress ? 
            <CircularProgress size={iconSize} thickness={6} /> : 
            <PlayArrow sx={{ fontSize: iconSize }} />,
          color: 'primary.main',
          bgColor: 'primary.light',
        };
      case 'completed':
        return {
          icon: <CheckCircle sx={{ fontSize: iconSize }} />,
          color: 'success.main',
          bgColor: 'success.light',
        };
      case 'error':
        return {
          icon: <Error sx={{ fontSize: iconSize }} />,
          color: 'error.main',
          bgColor: 'error.light',
        };
      case 'warning':
        return {
          icon: <Warning sx={{ fontSize: iconSize }} />,
          color: 'warning.main',
          bgColor: 'warning.light',
        };
      case 'info':
        return {
          icon: <Info sx={{ fontSize: iconSize }} />,
          color: 'info.main',
          bgColor: 'info.light',
        };
      case 'paused':
        return {
          icon: <Pause sx={{ fontSize: iconSize }} />,
          color: 'warning.main',
          bgColor: 'warning.light',
        };
      default:
        return {
          icon: <Info sx={{ fontSize: iconSize }} />,
          color: 'grey.500',
          bgColor: 'grey.100',
        };
    }
  };

  const config = getStatusConfig();
  const iconSize = getSizeValue();

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: iconSize + 8,
        height: iconSize + 8,
        borderRadius: '50%',
        bgcolor: config.bgColor,
        color: config.color,
        ...sx,
      }}
    >
      {config.icon}
    </Box>
  );
};

export default StatusIcon; 